import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from "react-router-dom"
import HOC from "./HOC"
import { BiDotsVerticalRounded } from "react-icons/bi";
import { FiArrowUpRight, FiPlus, FiSearch, FiTarget } from "react-icons/fi";
import BaseUrl from "../BaseUrl";
import axios from 'axios'
import { Menu } from '@headlessui/react'
import swal from "sweetalert";
import { getAuthConfig, getApiErrorMessage } from '../services/auth';

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

const Home = () => {
    const navigate = useNavigate()
    const [data, setData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoadingGoals, setIsLoadingGoals] = useState(true);

    const ViewGoal = async (row) => {
        try {
            navigate('/my-goal', {
                state: {
                    data: row.id
                }
            })
        } catch (error) {
            console.log(error);
        }
    }

    const updateGoalName = async (row) => {
        swal("Enter a new goal name:", {
            content: "input",
        })
            .then(async (value) => {
                const url = BaseUrl() + `api/goal/update-goal-name/${row.id}`;
                const data = {
                    name: value
                }
                try {
                    const res = await axios.post(url, data, getAuthConfig());
                    if (res.data.ResponseCode === 200) {
                        getMyGoal()
                        swal("Goal updated successfully", {
                            icon: "success"
                        });
                    } else {
                        swal(res.data.ResponseMessage, {
                            button: false
                        });
                    }
                } catch (error) {
                    swal(error.friendlyMessage || getApiErrorMessage(error), {
                        icon: "error",
                        button: false,
                    });
                }
            })
    };

    const deleteGoal = async (row) => {
        swal({
            title: "Delete goal?",
            text: "This action cannot be undone.",
            icon: "warning",
            buttons: true,
            dangerMode: true,
        }).then(async (willDelete) => {
            if (willDelete) {
                const url = BaseUrl() + 'api/goal/delete-goal/' + row.id
                try {
                    const res = await axios.post(url, null, getAuthConfig());
                    if (res.status === 200) {
                        getMyGoal()
                        swal("Goal deleted successfully", {
                            icon: "success"
                        });
                    } else {
                        swal(res.data.ResponseMessage, {
                            button: false
                        });
                    }
                } catch (error) {
                    swal(error.friendlyMessage || getApiErrorMessage(error), {
                        icon: "error",
                        button: false,
                    });
                }
            }
        })
    }

    const getMyGoal = async () => {
        const url = `${BaseUrl()}api/goal/get-my-goal`;
        setIsLoadingGoals(true);
        try {
            const res = await axios.get(url, getAuthConfig());
            if (res.data.ResponseCode === 200) {
                setData(res.data.Response.allgoal);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoadingGoals(false);
        }
    };

    useEffect(() => {
        getMyGoal();
    }, []);

    const filteredGoals = useMemo(() => {
        const query = searchTerm.trim().toLowerCase();
        if (!query) {
            return data;
        }

        return data.filter((item) => item.name?.toLowerCase().includes(query));
    }, [data, searchTerm]);

    return (
        <main className="mx-auto flex w-full max-w-7xl flex-col gap-4">
            <section className="relative overflow-hidden rounded-[1.9rem] border border-white/70 bg-gradient-to-br from-sky-500 via-blue-600 to-blue-800 px-4 py-3.5 text-white shadow-2xl shadow-sky-900/20 sm:px-5 sm:py-4">
                <div className="pointer-events-none absolute -right-16 -top-20 h-40 w-40 rounded-full bg-white/20 blur-3xl" />
                <div className="pointer-events-none absolute -bottom-24 left-8 h-36 w-36 rounded-full bg-cyan-300/20 blur-3xl" />
                <div className="relative flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div className="max-w-2xl">
                        <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-white/15 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-sky-100 ring-1 ring-white/20 backdrop-blur">
                            <FiTarget className="h-3.5 w-3.5" />
                            Goals Overview
                        </div>
                        <h2 className="text-xl font-bold tracking-tight sm:text-2xl">My goals</h2>
                        <p className="mt-1.5 max-w-xl text-sm leading-5 text-sky-100">
                            Review active goals, open details, rename milestones, or remove goals from one focused workspace.
                        </p>
                    </div>

                    <button
                        type="button"
                        onClick={() => navigate('/add-goal')}
                        className="inline-flex w-fit items-center justify-center gap-2 rounded-2xl bg-white px-3.5 py-2 text-sm font-bold text-blue-700 shadow-lg shadow-blue-950/10 transition hover:-translate-y-0.5 hover:bg-sky-50 focus:outline-none focus:ring-2 focus:ring-white/70"
                    >
                        <FiPlus className="h-4 w-4" />
                        Add Goal
                    </button>
                </div>

                <div className="relative mt-3 grid gap-2.5 sm:grid-cols-3">
                    <div className="rounded-2xl bg-white/15 px-3 py-2.5 ring-1 ring-white/20 backdrop-blur">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-sky-100">Total Goals</p>
                        <p className="mt-0.5 text-xl font-bold">{isLoadingGoals ? "..." : data.length}</p>
                    </div>
                    <div className="rounded-2xl bg-white/15 px-3 py-2.5 ring-1 ring-white/20 backdrop-blur">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-sky-100">Visible</p>
                        <p className="mt-0.5 text-xl font-bold">{isLoadingGoals ? "..." : filteredGoals.length}</p>
                    </div>
                    <div className="rounded-2xl bg-white/15 px-3 py-2.5 ring-1 ring-white/20 backdrop-blur">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-sky-100">Workspace</p>
                        <p className="mt-0.5 text-sm font-bold">Goal tracking</p>
                    </div>
                </div>
            </section>

            <section className="rounded-[1.75rem] border border-white/80 bg-white/90 p-3.5 shadow-xl shadow-sky-900/5 backdrop-blur sm:p-5">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-sky-600">Goal List</p>
                        <h3 className="mt-0.5 text-xl font-bold text-slate-900">Manage your goals</h3>
                    </div>

                    <label className="relative block w-full lg:max-w-xs">
                        <span className="sr-only">Search by goal name</span>
                        <FiSearch className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        <input
                            type="search"
                            placeholder="Search by goal name"
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                            className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-2.5 pl-10 pr-3.5 text-sm font-medium text-slate-800 outline-none transition placeholder:text-slate-400 hover:border-sky-200 focus:border-sky-400 focus:bg-white focus:ring-4 focus:ring-sky-100"
                        />
                    </label>
                </div>

                {isLoadingGoals ? (
                    <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                        {Array.from({ length: 6 }).map((_, index) => (
                            <article key={index} className="rounded-3xl border border-slate-200/80 bg-white p-4 shadow-sm">
                                <div className="flex items-start gap-3">
                                    <div className="h-10 w-10 shrink-0 rounded-2xl bg-sky-100 bsi-skeleton" />
                                    <div className="min-w-0 flex-1">
                                        <div className="h-3 w-20 rounded-full bg-sky-100 bsi-skeleton" />
                                        <div className="mt-2 h-5 w-3/4 rounded-full bg-slate-100 bsi-skeleton" />
                                    </div>
                                    <div className="h-9 w-9 rounded-2xl bg-slate-100 bsi-skeleton" />
                                </div>
                                <div className="mt-4 h-10 rounded-2xl bg-slate-50 bsi-skeleton" />
                            </article>
                        ))}
                    </div>
                ) : filteredGoals.length > 0 ? (
                    <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                        {filteredGoals.map((item, index) => (
                            <article
                                key={item.id}
                                className="group rounded-3xl border border-slate-200/80 bg-white p-4 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-sky-200 hover:shadow-xl hover:shadow-sky-900/10"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <button
                                        type="button"
                                        onClick={() => ViewGoal(item)}
                                        className="flex min-w-0 flex-1 items-start gap-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-300"
                                    >
                                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-100 to-blue-100 text-sky-700 ring-1 ring-sky-200/80">
                                            <FiTarget className="h-5 w-5" />
                                        </span>
                                        <span className="min-w-0">
                                            <span className="block text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                                                Goal {index + 1}
                                            </span>
                                            <span className="mt-0.5 block truncate text-base font-bold text-slate-900 transition group-hover:text-sky-700">
                                                {item.name}
                                            </span>
                                        </span>
                                    </button>

                                    <Menu as="div" className="relative">
                                        <Menu.Button className="inline-flex h-9 w-9 items-center justify-center rounded-2xl text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-300">
                                            <span className="sr-only">Open goal actions</span>
                                            <BiDotsVerticalRounded className="text-2xl" />
                                        </Menu.Button>

                                        <Menu.Items className="absolute right-0 z-20 mt-2 w-44 origin-top-right overflow-hidden rounded-2xl border border-slate-100 bg-white p-1 shadow-2xl shadow-slate-900/10 ring-1 ring-black/5 focus:outline-none">
                                            <Menu.Item>
                                                {({ active }) => (
                                                    <button
                                                        type="button"
                                                        onClick={() => ViewGoal(item)}
                                                        className={classNames(
                                                            active ? 'bg-sky-50 text-sky-700' : 'text-slate-700',
                                                            'block w-full rounded-xl px-4 py-2.5 text-left text-sm font-semibold transition'
                                                        )}
                                                    >
                                                        View
                                                    </button>
                                                )}
                                            </Menu.Item>
                                            <Menu.Item>
                                                {({ active }) => (
                                                    <button
                                                        type="button"
                                                        onClick={() => updateGoalName(item)}
                                                        className={classNames(
                                                            active ? 'bg-sky-50 text-sky-700' : 'text-slate-700',
                                                            'block w-full rounded-xl px-4 py-2.5 text-left text-sm font-semibold transition'
                                                        )}
                                                    >
                                                        Rename
                                                    </button>
                                                )}
                                            </Menu.Item>
                                            <Menu.Item>
                                                {({ active }) => (
                                                    <button
                                                        type="button"
                                                        onClick={() => deleteGoal(item)}
                                                        className={classNames(
                                                            active ? 'bg-red-50 text-red-600' : 'text-red-500',
                                                            'block w-full rounded-xl px-4 py-2.5 text-left text-sm font-semibold transition'
                                                        )}
                                                    >
                                                        Delete
                                                    </button>
                                                )}
                                            </Menu.Item>
                                        </Menu.Items>
                                    </Menu>
                                </div>

                                <div className="mt-4 flex items-center justify-between rounded-2xl bg-slate-50 px-3.5 py-2.5 text-sm">
                                    <span className="font-semibold text-slate-500">Open details</span>
                                    <button
                                        type="button"
                                        onClick={() => ViewGoal(item)}
                                        className="inline-flex items-center gap-1 font-bold text-sky-700 transition hover:text-blue-700"
                                    >
                                        View
                                        <FiArrowUpRight className="h-4 w-4" />
                                    </button>
                                </div>
                            </article>
                        ))}
                    </div>
                ) : (
                    <div className="mt-6 rounded-3xl border border-dashed border-sky-200 bg-sky-50/60 p-8 text-center">
                        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-sky-700 shadow-sm">
                            <FiTarget className="h-7 w-7" />
                        </div>
                        <h4 className="mt-4 text-xl font-bold text-slate-900">
                            {searchTerm ? 'No goals match your search' : 'No goals yet'}
                        </h4>
                        <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slate-500">
                            {searchTerm
                                ? 'Try a different goal name or clear the search field to see all goals.'
                                : 'Create your first goal to start tracking progress from this dashboard.'}
                        </p>
                        {!searchTerm && (
                            <button
                                type="button"
                                onClick={() => navigate('/add-goal')}
                                className="mt-5 inline-flex items-center justify-center gap-2 rounded-2xl bg-sky-600 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-sky-600/20 transition hover:-translate-y-0.5 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-sky-300"
                            >
                                <FiPlus className="h-5 w-5" />
                                Add Goal
                            </button>
                        )}
                    </div>
                )}
            </section>
        </main>
    )
}

export default HOC(Home);
